"use client";

import { useInitialContext } from "@repo/sdk";
import { useEmbeddedCheckoutContext } from "@repo/stripe/context/EmbeddedCheckoutContext";
import {
  ElementsThemeProvider,
  type ElementsTheme,
} from "@repo/stripe/context/ElementsThemeContext";
import ElementsCheckout from "@repo/stripe/ui-elements";
import { SummaryCard, type SummaryCardTheme } from "@repo/stripe/ui";
import * as Sentry from "@sentry/nextjs";
import React, { useEffect, useMemo } from "react";

import type {
  StripeCustomerID,
  StripeSubscriptionID,
} from "@repo/db/branded-types";
import { KinsaInitialContext } from "src/lib/definitions";
import { useTrustedUserEmail } from "src/lib/owlet-trusted-email";
import type { PricingPlanKeys } from "src/lib/pricing-plan-keys";
import { supportsExpressCheckout } from "src/lib/express-checkout-utils";
import { CheckoutExpressCheckout } from "./checkout-express-checkout";
import BrandedCheckbox from "src/components/branded-checkbox";
import { byBrand } from "@repo/tailwind-config/brand";
import { TOS_ROOT } from "src/lib/routes";

export interface StripeThemeConfig {
  appearance: {
    theme: "stripe" | "flat";
    variables: Record<string, string>;
    rules?: Record<string, Record<string, string>>;
  };
  fonts: Array<{ cssSrc: string }>;
  /** React FC components (page checkout) or raw font config strings (modal). */
  clientTypography: Record<string, unknown>;
  summaryCardTheme: SummaryCardTheme;
  /** Class for the submit button container (e.g. fixed vs sticky positioning). */
  submitFooterClassName: string;
  /** Brand-specific class overrides for the submit button (e.g. rounded, text casing). */
  submitButtonClassName: string;
}

interface PaymentCompleteData {
  customerId: StripeCustomerID;
  subscriptionId: StripeSubscriptionID;
}

/**
 * Props for StripeCheckoutContent
 */
export interface StripeCheckoutContentProps {
  /** Product key for the checkout */
  productKey: PricingPlanKeys;
  /** Theme configuration for Stripe */
  theme: StripeThemeConfig;
  /** Callback when payment completes inline */
  onPaymentComplete: (data: PaymentCompleteData) => void;
  /** Optional back button handler (for modal use) */
  onBack?: () => void;
  /** Optional back button label */
  backLabel?: string;
  /** Source identifier for Sentry breadcrumbs */
  source?: string;
  /** Additional metadata to include in user object */
  additionalMetadata?: Record<string, string>;
  /** Custom container className */
  containerClassName?: string;
  /** Content to render after the checkout form */
  children?: React.ReactNode;
  /** Where Stripe redirects after a successful express-checkout (Apple/Google
   *  Pay) wallet payment. Defaults to /home; pass the originating path (e.g.
   *  /intake/{id}) when the checkout is rendered outside the home flow. */
  successPath?: string;
}

/**
 * StripeCheckoutContent - Shared component for rendering Stripe Elements checkout.
 *
 * Used by:
 * - Payment page (/home steps/payment)
 * - Subscription gate modal
 *
 * Requires an EmbeddedCheckoutProvider ancestor for pricing plan state.
 */
export function StripeCheckoutContent({
  productKey,
  theme,
  onPaymentComplete,
  onBack,
  backLabel = "Back to plans",
  source = "checkout",
  additionalMetadata = {},
  containerClassName,
  children,
  successPath,
}: StripeCheckoutContentProps) {
  const context = useInitialContext<KinsaInitialContext>();
  const account = context?.account;
  // OwletAccount has no `email` field at runtime — only Kinsa's bridge
  // populates `account.email`. If this component is ever reused on Owlet
  // (it currently isn't), the bridge id_token JWT fallback keeps the
  // Stripe customer record carrying a real email instead of an empty
  // string that re-opens the empty-email-list trap upstream.
  const trustedEmail = useTrustedUserEmail();
  // PE-1538: promo-code field on the card checkout, Owlet-only. Kinsa never
  // renders it; the package stays brand-blind (field injected via theme prop).
  const enablePromoCodes = byBrand({ owlet: true, default: false });
  const {
    isLoading,
    error,
    currentPlan,
    isMonthlyPlan,
    setIsMonthlyPlan,
    monthlyPlan,
    yearlyPlan,
  } = useEmbeddedCheckoutContext();

  // Pre-fill the Stripe customer name from the account holder ("self") in
  // coveredPersons. Owlet's SDK historically doesn't ship a "self" entry —
  // only the child(ren) — so this often resolves to empty for Owlet. That's
  // intentional: better to leave Stripe customer.name blank at checkout than
  // stamp the child's name onto the billing record (the original PE-1100
  // bug). The post-create-profile sync (syncStripeCustomerFromProfile in
  // intake/owlet/actions.ts) writes the guardian's legalFirstName/lastName
  // to the customer once intake completes — only when the customer has no name
  // yet — so the Manage Billing screen converges to the right value.
  const accountHolder = account?.coveredPersons?.find(
    (p) => p.relationshipToAccountHolder === "self"
  );

  const fullName = accountHolder
    ? [accountHolder.firstName, accountHolder.lastName]
        .filter(Boolean)
        .join(" ")
    : "";

  useEffect(() => {
    if (isLoading) {
      Sentry.addBreadcrumb({
        message: `${source} loading - fetching pricing plans`,
        category: "stripe.elements",
        level: "info",
        data: {
          accountId: account?.id,
          accountEmail: account?.email,
          accountHolderName: fullName,
          source,
        },
      });
    } else if (error) {
      Sentry.addBreadcrumb({
        message: `${source} loading failed`,
        category: "stripe.elements",
        level: "error",
        data: {
          error,
          accountId: account?.id,
          source,
        },
      });
    } else {
      Sentry.addBreadcrumb({
        message: `${source} ready with Elements checkout`,
        category: "stripe.elements",
        level: "info",
        data: {
          accountId: account?.id,
          accountEmail: account?.email,
          source,
        },
      });
    }
  }, [isLoading, error, account?.id, account?.email, fullName, source]);

  const user = useMemo(() => {
    if (!account) return undefined;

    return {
      email: account.email || trustedEmail || "",
      name: fullName,
      metadata: {
        externalAccountId: account.id,
        selectedPlan: productKey,
        ...additionalMetadata,
      },
    };
  }, [account, fullName, productKey, additionalMetadata, trustedEmail]);

  return (
    <div className={containerClassName}>
      {onBack && (
        <div className="px-[24px] pt-[16px]">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-[8px] text-teal-500 hover:text-teal-400 transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[14px]">{backLabel}</span>
          </button>
        </div>
      )}

      <div className={onBack ? "px-[24px] pb-[24px]" : "px-[24px]"}>
        {!isLoading &&
          currentPlan.price !== null &&
          typeof theme.clientTypography?.HeadlineBodyText === "function" && (
            <SummaryCard
              isMonthly={isMonthlyPlan}
              setIsMonthly={setIsMonthlyPlan}
              clientTypography={
                theme.clientTypography as Record<
                  string,
                  React.FC<{ children: React.ReactNode; className?: string }>
                >
              }
              totalPrice={currentPlan.price}
              monthlyPrice={
                currentPlan.price !== null
                  ? currentPlan.price / (isMonthlyPlan ? 100 : 1200)
                  : null
              }
              trialDays={
                isMonthlyPlan
                  ? monthlyPlan.trialDays || 0
                  : yearlyPlan.trialDays || 0
              }
              taxes={0}
              theme={theme.summaryCardTheme}
              productName={currentPlan.name ?? productKey}
            />
          )}
        {/* Express Checkout (Apple Pay / Google Pay) — own Elements provider */}
        {user && currentPlan.id && currentPlan.price !== null && supportsExpressCheckout(productKey) && (
          <CheckoutExpressCheckout
            planDetails={{
              id: currentPlan.id,
              price: currentPlan.price,
              interval: isMonthlyPlan ? "month" : "year",
              trialDays: isMonthlyPlan
                ? monthlyPlan.trialDays || 0
                : yearlyPlan.trialDays || 0,
            }}
            productName={productKey}
            userInfo={user}
            successPath={successPath}
          />
        )}

        {user && (
          <ElementsThemeProvider
            appearance={theme.appearance}
            fonts={theme.fonts}
            enablePromoCodes={enablePromoCodes}
            errorClassName="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm"
            renderSubmitButton={({ isSubmitting, disabled, formId }) => (
              <div className={theme.submitFooterClassName}>
                <button
                  type="submit"
                  form={formId}
                  disabled={disabled}
                  className={`w-full bg-primary py-[15px] px-[24px] text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed ${theme.submitButtonClassName}`}
                >
                  {isSubmitting ? "Processing..." : "Submit Payment"}
                </button>
              </div>
            )}
            renderTosCheckbox={byBrand<ElementsTheme["renderTosCheckbox"]>({
              kinsa: ({ checked, onChange, error }) => (
                <label className="flex items-start gap-3 cursor-pointer py-3">
                  <BrandedCheckbox checked={checked} onChange={onChange} className="mt-0.5" />
                  <span
                    className={`text-xs leading-relaxed ${error ? "text-error" : "text-foreground-muted"}`}
                  >
                    I agree to the{" "}
                    <a
                      href={TOS_ROOT}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary-hover"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Kinsa Clinic Terms of Service
                    </a>
                  </span>
                </label>
              ),
              owlet: undefined,
            })}
          >
            <ElementsCheckout
              user={user}
              productName={currentPlan.name ?? productKey}
              productKey={productKey}
              onPaymentComplete={onPaymentComplete}
            />
          </ElementsThemeProvider>
        )}
      </div>

      {children}
    </div>
  );
}
